import { useState } from "react";
import { Gauge, ChevronDown } from "lucide-react";
import { useOpenState } from "@/components/SettingsSection";
import { VoiceCapacityCard } from "@/pages/dashboard/VoiceCapacityCard";
import { TextCapacityCard } from "@/pages/dashboard/TextCapacityCard";

const CHIP = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  watch: "bg-amber-50 text-amber-800 border-amber-200",
  high: "bg-red-50 text-red-700 border-red-200",
};

// Communities page (super admin): the voice + text capacity gauges behind ONE collapsed strip.
// The header keeps the two health chips visible; the chevron opens the full cards. The cards
// stay mounted (hidden) so they keep polling and feeding the header.
export function PlatformCapacityStrip() {
  const [open, toggle] = useOpenState("platform-capacity", false);
  const [sum, setSum] = useState({});
  const chip = (k, prefix) => sum[k] && (
    <span data-testid={`platform-capacity-${k}`} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold border rounded-full px-2.5 py-0.5 ${CHIP[sum[k].status] || CHIP.ok}`}>
      {prefix} {sum[k].label} <span className="font-normal opacity-80">· {sum[k].line}</span>
    </span>
  );
  return (
    <section data-testid="platform-capacity" className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
      <div role="button" tabIndex={0} aria-expanded={open} data-testid="platform-capacity-toggle" onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
        className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-sand-50 transition-colors select-none">
        <span className="h-8 w-8 rounded-xl bg-sand-100 flex items-center justify-center shrink-0"><Gauge className="h-4 w-4 text-ink/60" strokeWidth={1.75} /></span>
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Platform capacity</span>
          {chip("voice", "Voice:")}
          {chip("text", "Text:")}
        </div>
        <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </div>
      <div data-testid="platform-capacity-body" className={`${open ? "" : "hidden"} border-t border-sand-100 p-3 grid grid-cols-1 xl:grid-cols-2 gap-3`}>
        <VoiceCapacityCard onSummary={(v) => setSum((s) => ({ ...s, voice: v }))} />
        <TextCapacityCard onSummary={(v) => setSum((s) => ({ ...s, text: v }))} />
      </div>
    </section>
  );
}
